import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { save } from "../systems/Save";
import { sound } from "../systems/Sound";
import { menuButton } from "../ui/menuUi";
import { addQuitButton } from "../ui/menuUi";
import { THEME, panel } from "../ui/theme";

/** Settings modal rendered as its own scene above menus and launched pages. */
export class SettingsScene extends Phaser.Scene {
  private resetLayer?: Phaser.GameObjects.Container;

  constructor() { super("SettingsScene"); }

  create(): void {
    this.resetLayer = undefined;

    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7)
      .setOrigin(0)
      .setInteractive();
    dim.on("pointerdown", () => this.close());

    const w = 580, h = 500;
    const x = (GAME_WIDTH - w) / 2, y = (GAME_HEIGHT - h) / 2;
    panel(this, x, y, w, h, "parchment").setInteractive();
    addQuitButton(this, () => this.close(), x + w - 36, y + 34);

    this.add.text(GAME_WIDTH / 2, y + 46, "COMMAND SETTINGS", {
      fontFamily: THEME.fonts.display, fontSize: "28px", color: THEME.colors.iron,
    }).setOrigin(0.5);
    this.add.text(GAME_WIDTH / 2, y + 78, "Tune the hall before your next battle.", {
      fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.lock,
    }).setOrigin(0.5);

    const makeSlider = (label: string, rowY: number, getValue: () => number, setValue: (value: number) => void) => {
      const left = x + 58, trackX = x + 224, trackW = 250;
      this.add.text(left, rowY, label, {
        fontFamily: THEME.fonts.body, fontSize: "17px", color: THEME.colors.iron,
      }).setOrigin(0, 0.5);
      this.add.rectangle(trackX, rowY, trackW, 12, THEME.hex.iron, 0.72).setOrigin(0, 0.5);
      const fill = this.add.rectangle(trackX + 2, rowY, 2, 8, THEME.hex.gold, 0.95).setOrigin(0, 0.5);
      const knob = this.add.circle(trackX, rowY, 11, THEME.hex.gold).setStrokeStyle(2, THEME.hex.iron);
      const value = this.add.text(x + 506, rowY, "", {
        fontFamily: THEME.fonts.display, fontSize: "15px", color: THEME.colors.iron,
      }).setOrigin(0.5);
      const hit = this.add.zone(trackX + trackW / 2, rowY, trackW + 28, 44)
        .setInteractive({ useHandCursor: true });

      const refresh = () => {
        const amount = Phaser.Math.Clamp(getValue(), 0, 1);
        fill.width = Math.max(2, (trackW - 4) * amount);
        knob.x = trackX + (trackW - 4) * amount + 2;
        value.setText(`${Math.round(amount * 100)}%`);
      };
      const apply = (pointer: Phaser.Input.Pointer) => {
        setValue(Phaser.Math.Clamp((pointer.x - trackX) / trackW, 0, 1));
        refresh();
      };
      hit.on("pointerdown", apply);
      hit.on("pointermove", (pointer: Phaser.Input.Pointer) => { if (pointer.isDown) apply(pointer); });
      refresh();
    };

    const data = save.get();
    makeSlider("Master volume", y + 138, () => save.get().audio.master, value => sound.setMasterVolume(value));
    makeSlider("Music volume", y + 202, () => save.get().audio.music, value => sound.setMusicVolume(value));
    makeSlider("Effects volume", y + 266, () => save.get().audio.sfx, value => sound.setSfxVolume(value));

    const mute = menuButton(this, x + 166, y + 340, 210, 46, sound.isMuted() ? "UNMUTE ALL" : "MUTE ALL", {
      name: "settings-mute", fontSize: 15, display: true,
      onClick: () => {
        sound.toggleMuted();
        (mute.getData("label") as Phaser.GameObjects.Text).setText(sound.isMuted() ? "UNMUTE ALL" : "MUTE ALL");
      },
    });
    const lateNight = menuButton(this, x + 414, y + 340, 210, 46, data.audio.lateNight ? "NIGHT MODE: ON" : "NIGHT MODE: OFF", {
      name: "settings-night-mode", fontSize: 14, display: true,
      onClick: () => {
        sound.setLateNight(!save.get().audio.lateNight);
        (lateNight.getData("label") as Phaser.GameObjects.Text)
          .setText(save.get().audio.lateNight ? "NIGHT MODE: ON" : "NIGHT MODE: OFF");
      },
    });
    menuButton(this, x + 148, y + 424, 200, 42, "RESET PROGRESS", {
      name: "settings-reset", fontSize: 14, display: true, color: THEME.colors.blood,
      onClick: () => this.openResetConfirm(),
    });
    menuButton(this, x + 432, y + 424, 200, 42, "CLOSE", {
      name: "settings-close", fontSize: 15, display: true, onClick: () => this.close(),
    });

    const onEscape = () => this.resetLayer ? this.closeResetConfirm() : this.close();
    this.input.keyboard?.on("keydown-ESC", onEscape);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.input.keyboard?.off("keydown-ESC", onEscape));
  }

  private openResetConfirm(): void {
    if (this.resetLayer) return;
    const c = this.add.container(0, 0).setDepth(THEME.depths.overlay + 20);
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.76)
      .setOrigin(0)
      .setInteractive();
    const w = 460, h = 220, x = (GAME_WIDTH - w) / 2, y = (GAME_HEIGHT - h) / 2;
    c.add([dim, panel(this, x, y, w, h, "parchment")]);
    c.add(this.add.text(GAME_WIDTH / 2, y + 48, "RESET THE CAMPAIGN?", {
      fontFamily: THEME.fonts.display, fontSize: "25px", color: THEME.colors.iron,
    }).setOrigin(0.5));
    c.add(this.add.text(GAME_WIDTH / 2, y + 88, "This removes local progress and cannot be undone.", {
      fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.lock,
    }).setOrigin(0.5));
    const cancel = menuButton(this, x + 126, y + 166, 170, 46, "KEEP PROGRESS", {
      name: "reset-cancel", fontSize: 13, display: true, onClick: () => this.closeResetConfirm(),
    });
    const confirm = menuButton(this, x + 334, y + 166, 170, 46, "RESET", {
      name: "reset-confirm", fontSize: 15, display: true, color: THEME.colors.blood,
      onClick: () => {
        save.resetAll();
        this.close();
        if (this.scene.isActive("MenuScene")) this.scene.get("MenuScene").scene.restart();
        if (this.scene.isActive("MapSelectScene")) this.scene.get("MapSelectScene").scene.restart();
      },
    });
    c.add([cancel, confirm]);
    dim.on("pointerdown", () => this.closeResetConfirm());
    this.resetLayer = c;
  }

  private closeResetConfirm(): void {
    this.resetLayer?.destroy();
    this.resetLayer = undefined;
  }

  private close(): void {
    this.scene.stop();
  }
}
